import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { supabase } from '../lib/supabase';
import { Download, Loader2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { toCsv, downloadCsv } from '../lib/csv';
import { applyIssuePolicy, defaultPolicy } from '../lib/issuePolicy';
import { CATEGORIES } from './Issues';
import { buildLessonspaceRecordingUrl } from '../lib/lessonspace';
import {
  getPunctualityVerdict,
  getTutorLatenessSeconds,
  getStudentLatenessSeconds,
  DEFAULT_THRESHOLDS,
} from '../lib/punctuality';
import {
  resolveRubricDimensions,
  getDimensionRatings,
  ratingToScore,
  LEGACY_DIMENSION_KEYS,
  QUALITY_SELECT_COLUMNS,
} from '../lib/qualityDimensions';

const ROW_CAP = 10000;

const DATA_TYPES = [
  { key: 'lessons', label: 'Lessons', desc: 'One row per session' },
  { key: 'tutors', label: 'Tutors', desc: 'One row per tutor' },
  { key: 'students', label: 'Students', desc: 'One row per student' },
  { key: 'issues', label: 'Issues', desc: 'One row per flagged risk' },
  { key: 'punctuality', label: 'Punctuality', desc: 'Lateness incidents' },
];

const RANGE_PRESETS = [
  { key: '7d', label: 'Last 7d', days: 7 },
  { key: '28d', label: 'Last 28d', days: 28 },
  { key: '90d', label: 'Last 90d', days: 90 },
  { key: '1y', label: 'Last 1y', days: 365 },
  { key: 'all', label: 'All', days: null },
  { key: 'custom', label: 'Custom', days: 'custom' },
];

const QUALITY_BANDS = ['Any', 'Strong', 'Adequate', 'Needs Development'];
const ENGAGEMENT_BANDS = ['Any', 'High', 'Medium', 'Low'];
const STATUS_OPTIONS = [
  { key: 'any', label: 'Any' },
  { key: 'open', label: 'Open' },
  { key: 'under_review', label: 'Under Review' },
  { key: 'closed', label: 'Closed' },
];
const WHO_LATE_OPTIONS = [
  { key: 'either', label: 'Either' },
  { key: 'tutor', label: 'Tutor' },
  { key: 'student', label: 'Student' },
];

const ratingToPct = (r) => {
  const score = ratingToScore(r);
  return score > 0 ? Math.round((score / 3) * 100) : '';
};
const qualityBand = (avg) => {
  if (avg == null) return null;
  if (avg >= 2.5) return 'Strong';
  if (avg >= 1.5) return 'Adequate';
  return 'Needs Development';
};

const toIsoDate = (d) => d ? new Date(d).toISOString() : '';
const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const safeJson = (value) => {
  if (!value) return null;
  if (typeof value === 'object') return value;
  try { return JSON.parse(value); } catch { return null; }
};

// Per-dimension evidence for one quality_assessments row. New assessments carry
// evidence inside the `dimensions` JSONB; the flat `<key>_evidence` columns are
// only written for the five built-in keys, so they are the fallback.
const evidenceToText = (ev) => {
  if (!ev) return '';
  if (typeof ev === 'string') return ev;
  if (Array.isArray(ev)) {
    return ev
      .map(item => (typeof item === 'string' ? item : (item?.quote || item?.text || item?.evidence || '')))
      .filter(Boolean)
      .join('; ');
  }
  if (typeof ev === 'object') return ev.quote || ev.text || ev.evidence || '';
  return String(ev);
};

const getDimensionEvidence = (qa) => {
  const out = {};
  if (!qa) return out;
  const dims = safeJson(qa.dimensions);
  if (Array.isArray(dims)) {
    dims.forEach(d => {
      const key = typeof d?.key === 'string' ? d.key.trim() : '';
      if (!key) return;
      const text = evidenceToText(d?.evidence);
      if (text) out[key] = text;
    });
  }
  LEGACY_DIMENSION_KEYS.forEach(key => {
    if (!out[key] && qa[`${key}_evidence`]) out[key] = qa[`${key}_evidence`];
  });
  return out;
};

const getAnalysisStats = (analysisJson) => {
  const aj = safeJson(analysisJson);
  const reviewMoments = Array.isArray(aj?.review_worthy_moments) ? aj.review_worthy_moments : [];
  const positiveIndicators = Array.isArray(aj?.positive_indicators) ? aj.positive_indicators : [];
  const detected = positiveIndicators.filter(p => p?.detected !== false && (p?.indicator || p?.type));
  const enthusiasmSignals = detected.filter(p => (p.indicator || p.type) === 'student_enthusiasm');
  return {
    reviewMomentsCount: reviewMoments.length,
    enthusiasmSignalsCount: enthusiasmSignals.length,
    positiveDetectedCount: detected.length,
    positiveDetectedLabels: detected.map(p => p.indicator || p.type).join('; '),
    raw: aj,
  };
};

function resolveRange(rangeKey, customStart, customEnd) {
  if (rangeKey === 'all') return { gte: null, lte: null };
  if (rangeKey === 'custom') {
    return {
      gte: customStart ? new Date(customStart + 'T00:00:00').toISOString() : null,
      lte: customEnd ? new Date(customEnd + 'T23:59:59.999').toISOString() : null,
    };
  }
  const preset = RANGE_PRESETS.find(p => p.key === rangeKey);
  if (!preset || preset.days == null) return { gte: null, lte: null };
  const d = new Date();
  d.setDate(d.getDate() - preset.days);
  return { gte: d.toISOString(), lte: null };
}

// Everything up to the per-dimension block. The dimension columns themselves are
// generated from the org's rubric, so a custom rubric exports its own dimensions.
const LESSON_COLUMNS_HEAD = [
  { key: 'lesson_id', header: 'lesson_id', label: 'Lesson ID', group: 'Lesson' },
  { key: 'external_session_id', header: 'external_session_id', label: 'External session ID', group: 'Lesson' },
  { key: 'date', header: 'date', label: 'Date', group: 'Lesson' },
  { key: 'tutor', header: 'tutor', label: 'Tutor', group: 'Lesson' },
  { key: 'student', header: 'student', label: 'Student', group: 'Lesson' },
  { key: 'subject', header: 'subject', label: 'Subject', group: 'Lesson' },
  { key: 'duration_minutes', header: 'duration_minutes', label: 'Duration minutes', group: 'Lesson' },
  { key: 'processed_at', header: 'processed_at', label: 'Processed at', group: 'Processing' },
  { key: 'is_processed', header: 'is_processed', label: 'Processed', group: 'Processing' },
  { key: 'risk_level', header: 'risk_level', label: 'Risk level', group: 'Processing' },
  { key: 'student_engagement_level', header: 'student_engagement_level', label: 'Student engagement level', group: 'Processing' },
  { key: 'flagged_for_review', header: 'flagged_for_review', label: 'Flagged for review', group: 'Processing' },
  { key: 'review_note', header: 'review_note', label: 'Review note', group: 'Processing' },
  { key: 'lesson_summary', header: 'lesson_summary', label: 'Lesson summary', group: 'Summary' },
  { key: 'quality_overall', header: 'quality_overall', label: 'Quality band', group: 'Quality' },
  { key: 'quality_score_pct', header: 'quality_score_pct', label: 'Quality score %', group: 'Quality' },
  { key: 'quality_overall_summary', header: 'quality_overall_summary', label: 'Quality summary', group: 'Quality' },
  { key: 'coaching_suggestion', header: 'coaching_suggestion', label: 'Coaching suggestion', group: 'Quality' },
  { key: 'punctuality_status', header: 'punctuality_status', label: 'Punctuality status', group: 'Indicators' },
  { key: 'punctuality_severity', header: 'punctuality_severity', label: 'Punctuality severity', group: 'Indicators' },
  { key: 'punctuality_pct', header: 'punctuality_pct', label: 'Punctuality %', group: 'Indicators' },
  { key: 'tutor_late_seconds', header: 'tutor_late_seconds', label: 'Tutor late seconds', group: 'Indicators' },
  { key: 'tutor_late_minutes', header: 'tutor_late_minutes', label: 'Tutor late minutes', group: 'Indicators' },
  { key: 'student_late_seconds', header: 'student_late_seconds', label: 'Student late seconds', group: 'Indicators' },
  { key: 'student_late_minutes', header: 'student_late_minutes', label: 'Student late minutes', group: 'Indicators' },
];

const LESSON_COLUMNS_TAIL = [
  { key: 'open_issues_count', header: 'open_issues_count', label: 'Open issues count', group: 'Issues' },
  { key: 'suppressed_issues_count', header: 'suppressed_issues_count', label: 'Suppressed issues count', group: 'Issues' },
  { key: 'total_issues_count', header: 'total_issues_count', label: 'Total issues count', group: 'Issues' },
  { key: 'review_worthy_moments_count', header: 'review_worthy_moments_count', label: 'Review-worthy moments count', group: 'Student signals' },
  { key: 'enthusiasm_signals_count', header: 'enthusiasm_signals_count', label: 'Enthusiasm signals count', group: 'Student signals' },
  { key: 'positive_indicators_detected_count', header: 'positive_indicators_detected_count', label: 'Positive indicators count', group: 'Student signals' },
  { key: 'positive_indicators_detected', header: 'positive_indicators_detected', label: 'Positive indicators detected', group: 'Student signals' },
  { key: 'lessonspace_session_id', header: 'lessonspace_session_id', label: 'Lessonspace session ID', group: 'Links' },
  { key: 'lessonspace_recording_link', header: 'lessonspace_recording_link', label: 'Lessonspace recording link', group: 'Links' },
  { key: 'transcript_url', header: 'transcript_url', label: 'TutorTracer lesson URL', group: 'Links' },
  { key: 'analysis_json', header: 'analysis_json', label: 'Raw analysis JSON', group: 'Raw' },
];

const TUTOR_COLUMNS_HEAD = [
  { key: 'tutor_name', header: 'tutor_name', label: 'Tutor', group: 'Tutor' },
  { key: 'sessions_in_range', header: 'sessions_in_range', label: 'Sessions', group: 'Volume' },
  { key: 'hours_in_range', header: 'hours_in_range', label: 'Hours', group: 'Volume' },
  { key: 'avg_duration_minutes', header: 'avg_duration_minutes', label: 'Average duration minutes', group: 'Volume' },
  { key: 'first_session_date', header: 'first_session_date', label: 'First session date', group: 'Volume' },
  { key: 'last_session_date', header: 'last_session_date', label: 'Last session date', group: 'Volume' },
  { key: 'distinct_students', header: 'distinct_students', label: 'Distinct students', group: 'Volume' },
  { key: 'avg_quality_pct', header: 'avg_quality_pct', label: 'Average quality %', group: 'Quality' },
  { key: 'quality_band', header: 'quality_band', label: 'Quality band', group: 'Quality' },
];

const TUTOR_COLUMNS_TAIL = [
  { key: 'open_issues', header: 'open_issues', label: 'Open issues', group: 'Issues' },
  { key: 'suppressed_open_issues', header: 'suppressed_open_issues', label: 'Suppressed open issues', group: 'Issues' },
  { key: 'total_issues', header: 'total_issues', label: 'Total issues', group: 'Issues' },
  { key: 'issue_rate_pct', header: 'issue_rate_pct', label: 'Issue rate %', group: 'Issues' },
  { key: 'late_sessions_count', header: 'late_sessions_count', label: 'Late sessions count', group: 'Punctuality' },
  { key: 'late_sessions_pct', header: 'late_sessions_pct', label: 'Late sessions %', group: 'Punctuality' },
  { key: 'on_time_pct', header: 'on_time_pct', label: 'On-time %', group: 'Punctuality' },
  { key: 'review_worthy_moments_count', header: 'review_worthy_moments_count', label: 'Review moments count', group: 'Student signals' },
  { key: 'enthusiasm_signals_count', header: 'enthusiasm_signals_count', label: 'Enthusiasm signals count', group: 'Student signals' },
];

const STUDENT_COLUMNS = [
  { key: 'student_name', header: 'student_name', label: 'Student', group: 'Student' },
  { key: 'sessions_in_range', header: 'sessions_in_range', label: 'Sessions', group: 'Volume' },
  { key: 'hours_in_range', header: 'hours_in_range', label: 'Hours', group: 'Volume' },
  { key: 'avg_duration_minutes', header: 'avg_duration_minutes', label: 'Average duration minutes', group: 'Volume' },
  { key: 'distinct_tutors', header: 'distinct_tutors', label: 'Distinct tutors', group: 'Volume' },
  { key: 'first_session_date', header: 'first_session_date', label: 'First session date', group: 'Volume' },
  { key: 'last_session_date', header: 'last_session_date', label: 'Last session date', group: 'Volume' },
  { key: 'engagement_label', header: 'engagement_label', label: 'Engagement label', group: 'Engagement' },
  { key: 'engagement_high_count', header: 'engagement_high_count', label: 'High engagement count', group: 'Engagement' },
  { key: 'engagement_medium_count', header: 'engagement_medium_count', label: 'Medium engagement count', group: 'Engagement' },
  { key: 'engagement_low_count', header: 'engagement_low_count', label: 'Low engagement count', group: 'Engagement' },
  { key: 'engagement_high_pct', header: 'engagement_high_pct', label: 'High engagement %', group: 'Engagement' },
  { key: 'open_issues', header: 'open_issues', label: 'Open issues', group: 'Issues' },
  { key: 'suppressed_open_issues', header: 'suppressed_open_issues', label: 'Suppressed open issues', group: 'Issues' },
  { key: 'total_issues', header: 'total_issues', label: 'Total issues', group: 'Issues' },
  { key: 'review_worthy_moments_count', header: 'review_worthy_moments_count', label: 'Review moments count', group: 'Student signals' },
  { key: 'enthusiasm_signals_count', header: 'enthusiasm_signals_count', label: 'Enthusiasm signals count', group: 'Student signals' },
  { key: 'positive_indicators_detected_count', header: 'positive_indicators_detected_count', label: 'Positive indicators count', group: 'Student signals' },
];

const ISSUE_COLUMNS = [
  { key: 'issue_id', header: 'issue_id', label: 'Issue ID', group: 'Issue' },
  { key: 'lesson_id', header: 'lesson_id', label: 'Lesson ID', group: 'Issue' },
  { key: 'date', header: 'date', label: 'Lesson date', group: 'Issue' },
  { key: 'issue_created_at', header: 'issue_created_at', label: 'Issue created at', group: 'Issue' },
  { key: 'tutor', header: 'tutor', label: 'Tutor', group: 'People' },
  { key: 'student', header: 'student', label: 'Student', group: 'People' },
  { key: 'category', header: 'category', label: 'Category', group: 'Classification' },
  { key: 'severity', header: 'severity', label: 'Severity', group: 'Classification' },
  { key: 'status', header: 'status', label: 'Status', group: 'Workflow' },
  { key: 'summary', header: 'summary', label: 'Summary', group: 'Evidence' },
  { key: 'why_flagged', header: 'why_flagged', label: 'Why flagged', group: 'Evidence' },
  { key: 'tutor_quote', header: 'tutor_quote', label: 'Tutor quote', group: 'Evidence' },
  { key: 'context', header: 'context', label: 'Context', group: 'Evidence' },
  { key: 'timestamp_display', header: 'timestamp_display', label: 'Timestamp display', group: 'Evidence' },
  { key: 'timestamp_seconds', header: 'timestamp_seconds', label: 'Timestamp seconds', group: 'Evidence' },
  { key: 'care_level', header: 'care_level', label: 'Care level', group: 'Workflow' },
  { key: 'resolution_outcome', header: 'resolution_outcome', label: 'Resolution outcome', group: 'Workflow' },
  { key: 'resolution_notes', header: 'resolution_notes', label: 'Resolution notes', group: 'Workflow' },
  { key: 'actioned_by', header: 'actioned_by', label: 'Actioned by', group: 'Workflow' },
  { key: 'actioned_at', header: 'actioned_at', label: 'Actioned at', group: 'Workflow' },
  { key: 'resolved_at', header: 'resolved_at', label: 'Resolved at', group: 'Workflow' },
  { key: 'breach_count', header: 'breach_count', label: 'Policy breach count', group: 'Policy' },
  { key: 'last_breach_check_at', header: 'last_breach_check_at', label: 'Last breach check at', group: 'Policy' },
  { key: 'last_breach_check_result', header: 'last_breach_check_result', label: 'Last breach check result', group: 'Policy' },
  { key: 'suppressed_by_guardrails', header: 'suppressed_by_guardrails', label: 'Suppressed by guardrails', group: 'Guardrails' },
  { key: 'lesson_url', header: 'lesson_url', label: 'TutorTracer lesson URL', group: 'Links' },
];

const PUNCTUALITY_COLUMNS = [
  { key: 'lesson_id', header: 'lesson_id', label: 'Lesson ID', group: 'Lesson' },
  { key: 'external_session_id', header: 'external_session_id', label: 'External session ID', group: 'Lesson' },
  { key: 'date', header: 'date', label: 'Date', group: 'Lesson' },
  { key: 'tutor', header: 'tutor', label: 'Tutor', group: 'Lesson' },
  { key: 'student', header: 'student', label: 'Student', group: 'Lesson' },
  { key: 'subject', header: 'subject', label: 'Subject', group: 'Lesson' },
  { key: 'duration_minutes', header: 'duration_minutes', label: 'Duration minutes', group: 'Lesson' },
  { key: 'who_late', header: 'who_late', label: 'Who was late', group: 'Punctuality' },
  { key: 'punctuality_status', header: 'punctuality_status', label: 'Punctuality status', group: 'Punctuality' },
  { key: 'tutor_late_seconds', header: 'tutor_late_seconds', label: 'Tutor late seconds', group: 'Punctuality' },
  { key: 'tutor_late_minutes', header: 'tutor_late_minutes', label: 'Tutor late minutes', group: 'Punctuality' },
  { key: 'student_late_seconds', header: 'student_late_seconds', label: 'Student late seconds', group: 'Punctuality' },
  { key: 'student_late_minutes', header: 'student_late_minutes', label: 'Student late minutes', group: 'Punctuality' },
  { key: 'severity_label', header: 'severity_label', label: 'Severity label', group: 'Punctuality' },
  { key: 'lesson_url', header: 'lesson_url', label: 'TutorTracer lesson URL', group: 'Links' },
];

// Splice the per-dimension columns between head and tail, skipping any whose
// header would collide with a fixed column (a rubric could name a key "date").
const spliceDimensionColumns = (head, tail, dimensions, makeColumns) => {
  const taken = new Set([...head, ...tail].map(c => c.header));
  const middle = [];
  (Array.isArray(dimensions) ? dimensions : []).forEach(dim => {
    if (!dim || !dim.key) return;
    makeColumns(dim).forEach(col => {
      if (taken.has(col.header)) return;
      taken.add(col.header);
      middle.push(col);
    });
  });
  return [...head, ...middle, ...tail];
};

const buildLessonColumns = (dimensions) =>
  spliceDimensionColumns(LESSON_COLUMNS_HEAD, LESSON_COLUMNS_TAIL, dimensions, ({ key, label }) => [
    { key, header: key, label: `${label} rating`, group: 'Indicators' },
    { key: `${key}_score_pct`, header: `${key}_score_pct`, label: `${label} %`, group: 'Indicators' },
    { key: `${key}_evidence`, header: `${key}_evidence`, label: `${label} evidence`, group: 'Indicators' },
  ]);

const buildTutorColumns = (dimensions) =>
  spliceDimensionColumns(TUTOR_COLUMNS_HEAD, TUTOR_COLUMNS_TAIL, dimensions, ({ key, label }) => [
    { key: `${key}_pct`, header: `${key}_pct`, label: `${label} %`, group: 'Quality' },
  ]);

const buildColumnsByType = (dimensions) => ({
  lessons: buildLessonColumns(dimensions),
  tutors: buildTutorColumns(dimensions),
  students: STUDENT_COLUMNS,
  issues: ISSUE_COLUMNS,
  punctuality: PUNCTUALITY_COLUMNS,
});

const columnKeysFor = (columnsByType) => Object.fromEntries(
  Object.entries(columnsByType).map(([key, columns]) => [key, columns.map(c => c.key)])
);

const DEFAULT_DIMENSIONS = resolveRubricDimensions(null);
const DEFAULT_COLUMN_KEYS = columnKeysFor(buildColumnsByType(DEFAULT_DIMENSIONS));

const dimensionsSignature = (dimensions) => (dimensions || []).map(d => d.key).join('|');

const Export = () => {
  const { organizationId } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const [dataType, setDataType] = useState('lessons');
  const [rangeKey, setRangeKey] = useState('28d');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  const [lessonsFilters, setLessonsFilters] = useState({
    tutor: '', student: '', subject: '', hasIssues: 'any', qualityBand: 'Any',
  });
  const [tutorsFilters, setTutorsFilters] = useState({ minSessions: 0, onlyOpenIssues: false });
  const [studentsFilters, setStudentsFilters] = useState({ minSessions: 0, engagementBand: 'Any' });
  const [issuesFilters, setIssuesFilters] = useState({
    categories: CATEGORIES.map(c => c.key),
    minSeverity: 0, maxSeverity: 10,
    status: 'any',
    includeSuppressed: false,
  });
  const [punctualityFilters, setPunctualityFilters] = useState({ who: 'either', minMinutes: 1 });
  const [selectedColumnKeys, setSelectedColumnKeys] = useState(DEFAULT_COLUMN_KEYS);

  const [policy, setPolicy] = useState(defaultPolicy());
  const [thresholds, setThresholds] = useState(DEFAULT_THRESHOLDS);
  const [dimensions, setDimensions] = useState(DEFAULT_DIMENSIONS);

  const [previewRows, setPreviewRows] = useState([]);
  const [previewCount, setPreviewCount] = useState(0);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [capped, setCapped] = useState(false);
  const debounceRef = useRef(null);
  const reqRef = useRef(0);

  useEffect(() => {
    if (!organizationId) return;
    (async () => {
      const { data } = await supabase
        .from('organizations')
        .select('issue_filter_policy, lateness_minor_minutes, lateness_moderate_minutes, lateness_severe_minutes, quality_rubric')
        .eq('id', organizationId)
        .single();
      if (data) {
        setPolicy(data.issue_filter_policy || defaultPolicy());
        setThresholds({
          minor: data.lateness_minor_minutes ?? DEFAULT_THRESHOLDS.minor,
          moderate: data.lateness_moderate_minutes ?? DEFAULT_THRESHOLDS.moderate,
          severe: data.lateness_severe_minutes ?? DEFAULT_THRESHOLDS.severe,
        });
        setDimensions(resolveRubricDimensions(data.quality_rubric));
      }
    })();
  }, [organizationId]);

  const columnsByType = useMemo(() => buildColumnsByType(dimensions), [dimensions]);
  const dimensionsKey = dimensionsSignature(dimensions);
  const lastDimensionsKey = useRef(dimensionsSignature(DEFAULT_DIMENSIONS));

  // The rubric arrives after first render; when it differs from the built-in one
  // the dimension columns change identity, so re-select everything for the new set.
  useEffect(() => {
    if (lastDimensionsKey.current === dimensionsKey) return;
    lastDimensionsKey.current = dimensionsKey;
    setSelectedColumnKeys(columnKeysFor(columnsByType));
  }, [dimensionsKey, columnsByType]);

  const range = useMemo(() => resolveRange(rangeKey, customStart, customEnd), [rangeKey, customStart, customEnd]);
  const activeColumns = useMemo(() => {
    const keys = new Set(selectedColumnKeys[dataType] || []);
    return (columnsByType[dataType] || []).filter(c => keys.has(c.key));
  }, [dataType, selectedColumnKeys, columnsByType]);
  const activeColumnKeySet = useMemo(() => new Set(selectedColumnKeys[dataType] || []), [dataType, selectedColumnKeys]);

  const setAllColumnsForType = (type, on) => {
    setSelectedColumnKeys(prev => ({
      ...prev,
      [type]: on ? (columnsByType[type] || []).map(c => c.key) : [],
    }));
  };

  const toggleColumn = (type, key) => {
    setSelectedColumnKeys(prev => {
      const set = new Set(prev[type] || []);
      if (set.has(key)) set.delete(key);
      else set.add(key);
      return { ...prev, [type]: Array.from(set) };
    });
  };

  const buildRows = useCallback(async () => {
    if (!organizationId) return { rows: [], capped: false };

    const fetchSessionsIn = async (cols, extraFilter) => {
      let q = supabase
        .from('sessions')
        .select(cols)
        .eq('organization_id', organizationId)
        .eq('is_processed_v2', true)
        .order('lesson_timestamp', { ascending: false })
        .limit(ROW_CAP + 1);
      if (range.gte) q = q.gte('lesson_timestamp', range.gte);
      if (range.lte) q = q.lte('lesson_timestamp', range.lte);
      if (extraFilter) q = extraFilter(q);
      const { data, error } = await q;
      if (error) throw error;
      return data || [];
    };

    const batchIn = async (table, select, ids, batchSize = 200) => {
      if (!ids.length) return [];
      const out = [];
      for (let i = 0; i < ids.length; i += batchSize) {
        const chunk = ids.slice(i, i + batchSize);
        const { data, error } = await supabase.from(table).select(select).in('session_id', chunk);
        if (error) throw error;
        if (data) out.push(...data);
      }
      return out;
    };

    if (dataType === 'lessons') {
      const sessions = await fetchSessionsIn(
        'id, session_id, tutor_name, student_name, lesson_timestamp, subject_covered, duration_minutes, tutor_join_lateness_seconds, lateness_seconds, student_engagement_level, risk_level, flagged_for_review, review_note, analysis_json, lesson_summary, lessonspace_session_id, lessonspace_recording_link, processed_at, is_processed_v2',
        (q) => {
          const f = lessonsFilters;
          if (f.tutor) q = q.ilike('tutor_name', `%${f.tutor}%`);
          if (f.student) q = q.ilike('student_name', `%${f.student}%`);
          if (f.subject) q = q.ilike('subject_covered', `%${f.subject}%`);
          return q;
        }
      );
      const ids = sessions.map(s => s.id);
      const [qa, risks, summaries] = await Promise.all([
        batchIn('quality_assessments', `session_id, ${QUALITY_SELECT_COLUMNS}, clarity_evidence, engagement_evidence, adaptability_evidence, encouragement_evidence, structure_evidence, overall_summary, coaching_suggestion, insufficient_evidence, insufficient_reason`, ids),
        batchIn('risks_v2', 'id, session_id, category, severity, why_flagged, tutor_quote, context, status, filter_status, filter_decision_reason', ids),
        batchIn('session_summaries_v2', 'session_id, session_summary', ids),
      ]);
      const qaBy = {};
      qa.forEach(q => { qaBy[q.session_id] = q; });
      const summaryBy = {};
      summaries.forEach(s => { summaryBy[s.session_id] = s; });
      const risksBy = {};
      risks.forEach(r => {
        if (r.category === 'tutor_lateness') return;
        (risksBy[r.session_id] = risksBy[r.session_id] || []).push(r);
      });

      let rows = sessions.map(s => {
        const qaRow = qaBy[s.id];
        // Abstaining assessments export as not_assessed with no ratings
        const q = qaRow && !qaRow.insufficient_evidence ? qaRow : null;
        const ratings = q ? getDimensionRatings(q) : {};
        const evidence = q ? getDimensionEvidence(q) : {};
        const scores = dimensions.map(dim => ratingToScore(ratings[dim.key])).filter(x => x > 0);
        const avg = scores.length ? scores.reduce((a, b) => a + b, 0) / scores.length : null;
        const dimensionFields = {};
        dimensions.forEach(({ key }) => {
          dimensionFields[key] = ratings[key] || '';
          dimensionFields[`${key}_score_pct`] = ratingToPct(ratings[key]);
          dimensionFields[`${key}_evidence`] = evidence[key] || '';
        });
        const allRisks = risksBy[s.id] || [];
        const openRisks = allRisks.filter(r => !r.status || r.status === 'new' || r.status === 'under_review');
        const { visible, suppressed } = applyIssuePolicy(openRisks, policy);
        const tutorLate = getTutorLatenessSeconds(s);
        const studentLate = getStudentLatenessSeconds(s);
        const verdict = getPunctualityVerdict(s, thresholds);
        const analysis = getAnalysisStats(s.analysis_json);
        return {
          ...dimensionFields,
          lesson_id: s.id,
          external_session_id: s.session_id || '',
          date: toIsoDate(s.lesson_timestamp),
          tutor: s.tutor_name || '',
          student: s.student_name || '',
          subject: s.subject_covered || '',
          duration_minutes: s.duration_minutes ?? '',
          processed_at: toIsoDate(s.processed_at),
          is_processed: !!s.is_processed_v2,
          risk_level: s.risk_level || '',
          student_engagement_level: s.student_engagement_level || '',
          flagged_for_review: !!s.flagged_for_review,
          review_note: s.review_note || '',
          lesson_summary: summaryBy[s.id]?.session_summary || s.lesson_summary || '',
          quality_overall: qualityBand(avg) || (qaRow?.insufficient_evidence ? 'not_assessed' : ''),
          quality_score_pct: avg != null ? Math.round((avg / 3) * 100) : '',
          quality_overall_summary: q?.overall_summary || qaRow?.insufficient_reason || '',
          coaching_suggestion: q?.coaching_suggestion || '',
          punctuality_status: verdict.status || '',
          punctuality_severity: verdict.severity || '',
          punctuality_pct: verdict.status === 'on_time' ? 100 : 0,
          tutor_late_seconds: tutorLate,
          tutor_late_minutes: Math.round((tutorLate / 60) * 10) / 10,
          student_late_seconds: studentLate,
          student_late_minutes: Math.round((studentLate / 60) * 10) / 10,
          open_issues_count: visible.length,
          suppressed_issues_count: suppressed.length,
          total_issues_count: allRisks.length,
          review_worthy_moments_count: analysis.reviewMomentsCount,
          enthusiasm_signals_count: analysis.enthusiasmSignalsCount,
          positive_indicators_detected_count: analysis.positiveDetectedCount,
          positive_indicators_detected: analysis.positiveDetectedLabels,
          lessonspace_session_id: s.lessonspace_session_id || '',
          lessonspace_recording_link: s.lessonspace_recording_link ? buildLessonspaceRecordingUrl({ recordingLink: s.lessonspace_recording_link }) : '',
          transcript_url: `${window.location.origin}/lessons/${s.id}`,
          analysis_json: analysis.raw || '',
        };
      });

      const f = lessonsFilters;
      if (f.hasIssues === 'yes') rows = rows.filter(r => r.open_issues_count > 0);
      else if (f.hasIssues === 'no') rows = rows.filter(r => r.open_issues_count === 0);
      if (f.qualityBand !== 'Any') rows = rows.filter(r => r.quality_overall === f.qualityBand);

      const wasCapped = rows.length > ROW_CAP;
      return { rows: rows.slice(0, ROW_CAP), capped: wasCapped };
    }

    if (dataType === 'tutors' || dataType === 'students') {
      const sessions = await fetchSessionsIn(
        'id, tutor_name, student_name, lesson_timestamp, duration_minutes, tutor_join_lateness_seconds, lateness_seconds, student_engagement_level, analysis_json'
      );
      const ids = sessions.map(s => s.id);

      if (dataType === 'tutors') {
        const [qa, risks] = await Promise.all([
          batchIn('quality_assessments', `session_id, ${QUALITY_SELECT_COLUMNS}, insufficient_evidence`, ids),
          batchIn('risks_v2', 'id, session_id, category, severity, why_flagged, tutor_quote, context, status, filter_status, filter_decision_reason', ids),
        ]);
        const qaBy = {};
        // Skip abstaining assessments (insufficient transcript evidence)
        qa.forEach(q => { if (!q.insufficient_evidence) qaBy[q.session_id] = q; });
        const sessionToTutor = {};
        sessions.forEach(s => { sessionToTutor[s.id] = s.tutor_name; });

        const openRisksPerTutor = {};
        const allRisksPerTutor = {};
        risks.forEach(r => {
          if (r.category === 'tutor_lateness') return;
          const tutor = sessionToTutor[r.session_id];
          if (!tutor) return;
          (allRisksPerTutor[tutor] = allRisksPerTutor[tutor] || []).push(r);
          if (r.status && r.status !== 'new' && r.status !== 'under_review') return;
          (openRisksPerTutor[tutor] = openRisksPerTutor[tutor] || []).push(r);
        });
        const visibleByTutor = {};
        const suppressedByTutor = {};
        for (const [tutor, list] of Object.entries(openRisksPerTutor)) {
          const { visible, suppressed } = applyIssuePolicy(list, policy);
          visibleByTutor[tutor] = visible.length;
          suppressedByTutor[tutor] = suppressed.length;
        }

        const map = {};
        sessions.forEach(s => {
          const name = s.tutor_name;
          if (!name) return;
          if (!map[name]) {
            map[name] = {
              sessions: 0, minutes: 0, students: new Set(), qaScores: [], lateCount: 0,
              firstDate: null, lastDate: null, dimTotals: {}, qaCount: 0,
              reviewMoments: 0, enthusiasmSignals: 0,
            };
          }
          const m = map[name];
          m.sessions++;
          m.minutes += s.duration_minutes || 0;
          if (s.student_name) m.students.add(s.student_name);
          if (s.lesson_timestamp && (!m.firstDate || s.lesson_timestamp < m.firstDate)) m.firstDate = s.lesson_timestamp;
          if (s.lesson_timestamp && (!m.lastDate || s.lesson_timestamp > m.lastDate)) m.lastDate = s.lesson_timestamp;
          const analysis = getAnalysisStats(s.analysis_json);
          m.reviewMoments += analysis.reviewMomentsCount;
          m.enthusiasmSignals += analysis.enthusiasmSignalsCount;
          const q = qaBy[s.id];
          if (q) {
            const ratings = getDimensionRatings(q);
            const scores = dimensions.map(dim => ratingToScore(ratings[dim.key])).filter(x => x > 0);
            if (scores.length) m.qaScores.push(scores.reduce((a, b) => a + b, 0) / scores.length);
            dimensions.forEach(({ key }) => {
              m.dimTotals[key] = (m.dimTotals[key] || 0) + ratingToScore(ratings[key]);
            });
            m.qaCount++;
          }
          const verdict = getPunctualityVerdict(s, thresholds);
          if (verdict.status === 'late') m.lateCount++;
        });

        const pctVal = (v, n) => n > 0 ? Math.round((v / n / 3) * 100) : 0;
        let rows = Object.entries(map).map(([name, m]) => ({
          ...Object.fromEntries(dimensions.map(({ key }) => [
            `${key}_pct`, pctVal(m.dimTotals[key] || 0, m.qaCount),
          ])),
          tutor_name: name,
          sessions_in_range: m.sessions,
          hours_in_range: Math.round((m.minutes / 60) * 10) / 10,
          avg_duration_minutes: m.sessions ? Math.round((m.minutes / m.sessions) * 10) / 10 : 0,
          first_session_date: toIsoDate(m.firstDate),
          last_session_date: toIsoDate(m.lastDate),
          avg_quality_pct: m.qaScores.length ? Math.round((m.qaScores.reduce((a, b) => a + b, 0) / m.qaScores.length / 3) * 100) : 0,
          quality_band: qualityBand(m.qaScores.length ? m.qaScores.reduce((a, b) => a + b, 0) / m.qaScores.length : null) || '',
          open_issues: visibleByTutor[name] || 0,
          suppressed_open_issues: suppressedByTutor[name] || 0,
          total_issues: allRisksPerTutor[name]?.length || 0,
          issue_rate_pct: m.sessions ? Math.round(((visibleByTutor[name] || 0) / m.sessions) * 100) : 0,
          late_sessions_count: m.lateCount,
          late_sessions_pct: m.sessions ? Math.round((m.lateCount / m.sessions) * 100) : 0,
          on_time_pct: m.sessions ? Math.round(((m.sessions - m.lateCount) / m.sessions) * 100) : 100,
          distinct_students: m.students.size,
          review_worthy_moments_count: m.reviewMoments,
          enthusiasm_signals_count: m.enthusiasmSignals,
        })).sort((a, b) => b.sessions_in_range - a.sessions_in_range);

        const f = tutorsFilters;
        if (f.minSessions > 0) rows = rows.filter(r => r.sessions_in_range >= f.minSessions);
        if (f.onlyOpenIssues) rows = rows.filter(r => r.open_issues > 0);

        const wasCapped = rows.length > ROW_CAP;
        return { rows: rows.slice(0, ROW_CAP), capped: wasCapped };
      }

      const [studentRisks] = await Promise.all([
        batchIn('risks_v2', 'id, session_id, category, severity, status, why_flagged, tutor_quote, context, filter_status, filter_decision_reason', ids),
      ]);
      const sessionToStudent = {};
      sessions.forEach(s => { sessionToStudent[s.id] = s.student_name; });
      const openRisksPerStudent = {};
      const allRisksPerStudent = {};
      studentRisks.forEach(r => {
        if (r.category === 'tutor_lateness') return;
        const student = sessionToStudent[r.session_id];
        if (!student) return;
        (allRisksPerStudent[student] = allRisksPerStudent[student] || []).push(r);
        if (r.status && r.status !== 'new' && r.status !== 'under_review') return;
        (openRisksPerStudent[student] = openRisksPerStudent[student] || []).push(r);
      });
      const visibleByStudent = {};
      const suppressedByStudent = {};
      for (const [student, list] of Object.entries(openRisksPerStudent)) {
        const { visible, suppressed } = applyIssuePolicy(list, policy);
        visibleByStudent[student] = visible.length;
        suppressedByStudent[student] = suppressed.length;
      }

      const map = {};
      sessions.forEach(s => {
        const name = s.student_name;
        if (!name) return;
        if (!map[name]) {
          map[name] = {
            sessions: 0, minutes: 0, tutors: new Set(), firstDate: null, lastDate: null,
            engHigh: 0, engMed: 0, engLow: 0, reviewMoments: 0, enthusiasmSignals: 0, positiveDetected: 0,
          };
        }
        const m = map[name];
        m.sessions++;
        m.minutes += s.duration_minutes || 0;
        if (s.tutor_name) m.tutors.add(s.tutor_name);
        if (s.lesson_timestamp && (!m.firstDate || s.lesson_timestamp < m.firstDate)) m.firstDate = s.lesson_timestamp;
        if (s.lesson_timestamp && (!m.lastDate || s.lesson_timestamp > m.lastDate)) m.lastDate = s.lesson_timestamp;
        const eng = (s.student_engagement_level || '').toLowerCase();
        if (eng === 'high') m.engHigh++;
        else if (eng === 'medium') m.engMed++;
        else if (eng === 'low') m.engLow++;
        const analysis = getAnalysisStats(s.analysis_json);
        m.reviewMoments += analysis.reviewMomentsCount;
        m.enthusiasmSignals += analysis.enthusiasmSignalsCount;
        m.positiveDetected += analysis.positiveDetectedCount;
      });

      let rows = Object.entries(map).map(([name, m]) => {
        const total = m.engHigh + m.engMed + m.engLow;
        let label = 'N/A';
        if (total > 0) {
          if (m.engHigh / total >= 0.5) label = 'High';
          else if ((m.engHigh + m.engMed) / total >= 0.5) label = 'Medium';
          else label = 'Low';
        }
        return {
          student_name: name,
          sessions_in_range: m.sessions,
          hours_in_range: Math.round((m.minutes / 60) * 10) / 10,
          avg_duration_minutes: m.sessions ? Math.round((m.minutes / m.sessions) * 10) / 10 : 0,
          distinct_tutors: m.tutors.size,
          first_session_date: toIsoDate(m.firstDate),
          last_session_date: toIsoDate(m.lastDate),
          engagement_label: label,
          engagement_high_count: m.engHigh,
          engagement_medium_count: m.engMed,
          engagement_low_count: m.engLow,
          engagement_high_pct: total ? Math.round((m.engHigh / total) * 100) : 0,
          open_issues: visibleByStudent[name] || 0,
          suppressed_open_issues: suppressedByStudent[name] || 0,
          total_issues: allRisksPerStudent[name]?.length || 0,
          review_worthy_moments_count: m.reviewMoments,
          enthusiasm_signals_count: m.enthusiasmSignals,
          positive_indicators_detected_count: m.positiveDetected,
        };
      }).sort((a, b) => b.sessions_in_range - a.sessions_in_range);

      const f = studentsFilters;
      if (f.minSessions > 0) rows = rows.filter(r => r.sessions_in_range >= f.minSessions);
      if (f.engagementBand !== 'Any') rows = rows.filter(r => r.engagement_label === f.engagementBand);

      const wasCapped = rows.length > ROW_CAP;
      return { rows: rows.slice(0, ROW_CAP), capped: wasCapped };
    }

    if (dataType === 'issues') {
      const f = issuesFilters;
      let q = supabase
        .from('risks_v2')
        .select('id, session_id, category, severity, status, summary, why_flagged, tutor_quote, context, filter_status, filter_decision_reason, timestamp_display, timestamp_seconds, care_level, resolution_outcome, resolution_notes, actioned_by, actioned_at, resolved_at, breach_count, last_breach_check_at, last_breach_check_result, created_at, sessions!inner(id, tutor_name, student_name, lesson_timestamp)')
        .eq('organization_id', organizationId)
        .neq('category', 'tutor_lateness')
        .order('created_at', { ascending: false })
        .limit(ROW_CAP + 1);

      if (range.gte) q = q.gte('created_at', range.gte);
      if (range.lte) q = q.lte('created_at', range.lte);

      if (f.categories.length && f.categories.length !== CATEGORIES.length) {
        q = q.in('category', f.categories);
      }
      if (f.minSeverity > 0) q = q.gte('severity', f.minSeverity);
      if (f.maxSeverity < 10) q = q.lte('severity', f.maxSeverity);
      if (f.status === 'open') q = q.or('status.is.null,status.eq.new');
      else if (f.status === 'under_review') q = q.eq('status', 'under_review');
      else if (f.status === 'closed') q = q.in('status', ['resolved', 'dismissed']);

      const { data, error } = await q;
      if (error) throw error;
      const all = data || [];

      const partitioned = applyIssuePolicy(all, policy);
      const visibleArr = partitioned.visible;
      const suppressedArr = partitioned.suppressed;
      const include = f.includeSuppressed ? all : visibleArr;
      const suppressedIds = new Set(suppressedArr.map(r => r.id));

      const rows = include.map(r => ({
        issue_id: r.id,
        lesson_id: r.session_id,
        date: toIsoDate(r.sessions?.lesson_timestamp || r.created_at),
        issue_created_at: toIsoDate(r.created_at),
        tutor: r.sessions?.tutor_name || '',
        student: r.sessions?.student_name || '',
        category: r.category || '',
        severity: r.severity ?? '',
        status: r.status || 'open',
        summary: r.summary || '',
        why_flagged: r.why_flagged || '',
        tutor_quote: r.tutor_quote || '',
        context: r.context || '',
        timestamp_display: r.timestamp_display || '',
        timestamp_seconds: r.timestamp_seconds ?? '',
        care_level: r.care_level ?? '',
        resolution_outcome: r.resolution_outcome || '',
        resolution_notes: r.resolution_notes || '',
        actioned_by: r.actioned_by || '',
        actioned_at: toIsoDate(r.actioned_at),
        resolved_at: toIsoDate(r.resolved_at),
        breach_count: r.breach_count ?? '',
        last_breach_check_at: toIsoDate(r.last_breach_check_at),
        last_breach_check_result: r.last_breach_check_result || '',
        suppressed_by_guardrails: suppressedIds.has(r.id),
        lesson_url: `${window.location.origin}/lessons/${r.session_id}`,
      }));

      const wasCapped = rows.length > ROW_CAP;
      return { rows: rows.slice(0, ROW_CAP), capped: wasCapped };
    }

    if (dataType === 'punctuality') {
      const sessions = await fetchSessionsIn(
        'id, session_id, tutor_name, student_name, lesson_timestamp, subject_covered, duration_minutes, tutor_join_lateness_seconds, lateness_seconds'
      );
      const f = punctualityFilters;
      const minSec = Math.max(0, Number(f.minMinutes) || 0) * 60;

      let rows = sessions.map(s => {
        const tutorLate = getTutorLatenessSeconds(s);
        const studentLate = getStudentLatenessSeconds(s);
        const verdict = getPunctualityVerdict(s, thresholds);
        let who = 'none';
        if (tutorLate >= minSec && studentLate >= minSec) who = 'both';
        else if (tutorLate >= minSec) who = 'tutor';
        else if (studentLate >= minSec) who = 'student';

        return {
          lesson_id: s.id,
          external_session_id: s.session_id || '',
          date: toIsoDate(s.lesson_timestamp),
          tutor: s.tutor_name || '',
          student: s.student_name || '',
          subject: s.subject_covered || '',
          duration_minutes: s.duration_minutes ?? '',
          who_late: who,
          punctuality_status: verdict.status || '',
          tutor_late_seconds: tutorLate,
          tutor_late_minutes: Math.round((tutorLate / 60) * 10) / 10,
          student_late_seconds: studentLate,
          student_late_minutes: Math.round((studentLate / 60) * 10) / 10,
          severity_label: verdict.severity || (verdict.status === 'on_time' ? 'on_time' : ''),
          lesson_url: `${window.location.origin}/lessons/${s.id}`,
          _tutorLate: tutorLate,
          _studentLate: studentLate,
        };
      });

      rows = rows.filter(r => r._tutorLate >= minSec || r._studentLate >= minSec);
      if (f.who === 'tutor') rows = rows.filter(r => r._tutorLate >= minSec);
      else if (f.who === 'student') rows = rows.filter(r => r._studentLate >= minSec);

      rows.forEach(r => { delete r._tutorLate; delete r._studentLate; });
      const wasCapped = rows.length > ROW_CAP;
      return { rows: rows.slice(0, ROW_CAP), capped: wasCapped };
    }

    return { rows: [], capped: false };
  }, [organizationId, dataType, range, lessonsFilters, tutorsFilters, studentsFilters, issuesFilters, punctualityFilters, policy, thresholds, dimensions]);

  useEffect(() => {
    if (!organizationId) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setPreviewLoading(true);
    setPreviewError(null);
    debounceRef.current = setTimeout(async () => {
      const reqId = ++reqRef.current;
      try {
        const result = await buildRows();
        if (reqId !== reqRef.current) return;
        setPreviewRows(result.rows);
        setPreviewCount(result.rows.length);
        setCapped(result.capped);
      } catch (err) {
        if (reqId !== reqRef.current) return;
        console.error('Export preview error:', err);
        setPreviewError(err.message || 'Failed to compute preview');
        setPreviewRows([]);
        setPreviewCount(0);
        setCapped(false);
      } finally {
        if (reqId === reqRef.current) setPreviewLoading(false);
      }
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [organizationId, buildRows]);

  const handleDownload = () => {
    const csv = toCsv(previewRows, activeColumns);
    const filename = `tutortracer-${dataType}-${today()}.csv`;
    downloadCsv(filename, csv);
  };

  const card = `rounded-lg border ${isDark ? 'bg-[#121212] border-[#27272a]' : 'bg-white border-[#e5e5e5]'}`;
  const muted = isDark ? 'text-[#a1a1aa]' : 'text-[#666666]';
  const subtle = isDark ? 'text-[#52525b]' : 'text-[#999999]';
  const heading = isDark ? 'text-white' : 'text-[#1a1a1a]';
  const chipBase = `px-3 py-1.5 text-xs font-medium rounded-md transition-colors`;
  const chipActive = isDark ? 'bg-[#27272a] text-white' : 'bg-[#e8e8e8] text-[#1a1a1a]';
  const chipIdle = isDark ? 'text-[#a1a1aa] hover:text-white' : 'text-[#666666] hover:text-[#1a1a1a]';
  const chipWrap = `flex items-center rounded-lg border p-1 gap-0.5 ${isDark ? 'border-[#27272a] bg-[#121212]' : 'border-[#e5e5e5] bg-white'}`;
  const stepLabel = `text-xs font-medium uppercase tracking-wider ${subtle}`;

  return (
    <div className="space-y-6">
      <div>
        <h1 className={`text-2xl font-semibold font-[Manrope] tracking-tight ${heading}`}>Export</h1>
        <p className={`text-sm mt-1 ${muted}`}>
          Download lessons, tutors, students, issues, or punctuality data as CSV. Capped at {ROW_CAP.toLocaleString()} rows per export.
        </p>
      </div>

      <div className={`${card} p-5 space-y-3`}>
        <div className={stepLabel}>Step 1 — Data type</div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          {DATA_TYPES.map(t => (
            <button
              key={t.key}
              onClick={() => setDataType(t.key)}
              className={`text-left px-3 py-3 rounded-lg border transition-colors ${
                dataType === t.key
                  ? isDark ? 'bg-[#18181b] border-[#3f3f46] text-white' : 'bg-[#f5f5f5] border-[#cccccc] text-[#1a1a1a]'
                  : isDark ? 'border-[#27272a] hover:border-[#3f3f46] text-[#a1a1aa]' : 'border-[#e5e5e5] hover:border-[#cccccc] text-[#666]'
              }`}
            >
              <div className="text-sm font-medium">{t.label}</div>
              <div className={`text-xs mt-0.5 ${subtle}`}>{t.desc}</div>
            </button>
          ))}
        </div>
      </div>

      <div className={`${card} p-5 space-y-3`}>
        <div className={stepLabel}>Step 2 — Date range</div>
        <div className="flex flex-wrap items-center gap-3">
          <div className={chipWrap}>
            {RANGE_PRESETS.map(p => (
              <button
                key={p.key}
                onClick={() => setRangeKey(p.key)}
                className={`${chipBase} ${rangeKey === p.key ? chipActive : chipIdle}`}
              >
                {p.label}
              </button>
            ))}
          </div>
          {rangeKey === 'custom' && (
            <div className="flex items-center gap-2">
              <Input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)} className="h-9 w-40" />
              <span className={subtle}>to</span>
              <Input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)} className="h-9 w-40" />
            </div>
          )}
          <div className={`text-xs ${subtle}`}>
            {dataType === 'issues' ? 'Filters risks_v2.created_at' : 'Filters sessions.lesson_timestamp'}
          </div>
        </div>
      </div>

      <div className={`${card} p-5 space-y-4`}>
        <div className={stepLabel}>Step 3 — Filters</div>
        {dataType === 'lessons' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <LabeledField label="Tutor name contains" isDark={isDark}>
              <Input value={lessonsFilters.tutor} onChange={e => setLessonsFilters(f => ({ ...f, tutor: e.target.value }))} placeholder="e.g. Jane" />
            </LabeledField>
            <LabeledField label="Student name contains" isDark={isDark}>
              <Input value={lessonsFilters.student} onChange={e => setLessonsFilters(f => ({ ...f, student: e.target.value }))} placeholder="e.g. Alex" />
            </LabeledField>
            <LabeledField label="Subject contains" isDark={isDark}>
              <Input value={lessonsFilters.subject} onChange={e => setLessonsFilters(f => ({ ...f, subject: e.target.value }))} placeholder="e.g. Algebra" />
            </LabeledField>
            <LabeledField label="Has issues" isDark={isDark}>
              <div className={chipWrap}>
                {['any', 'yes', 'no'].map(opt => (
                  <button key={opt} onClick={() => setLessonsFilters(f => ({ ...f, hasIssues: opt }))}
                    className={`${chipBase} ${lessonsFilters.hasIssues === opt ? chipActive : chipIdle}`}>
                    {opt}
                  </button>
                ))}
              </div>
            </LabeledField>
            <LabeledField label="Quality band" isDark={isDark}>
              <div className={chipWrap}>
                {QUALITY_BANDS.map(b => (
                  <button key={b} onClick={() => setLessonsFilters(f => ({ ...f, qualityBand: b }))}
                    className={`${chipBase} ${lessonsFilters.qualityBand === b ? chipActive : chipIdle}`}>
                    {b}
                  </button>
                ))}
              </div>
            </LabeledField>
          </div>
        )}

        {dataType === 'tutors' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <LabeledField label="Minimum sessions in range" isDark={isDark}>
              <Input type="number" min={0} value={tutorsFilters.minSessions}
                onChange={e => setTutorsFilters(f => ({ ...f, minSessions: Number(e.target.value) || 0 }))} className="w-32" />
            </LabeledField>
            <LabeledField label="Only tutors with open issues" isDark={isDark}>
              <label className="flex items-center gap-2">
                <Checkbox checked={tutorsFilters.onlyOpenIssues}
                  onCheckedChange={(v) => setTutorsFilters(f => ({ ...f, onlyOpenIssues: !!v }))} />
                <span className={`text-sm ${muted}`}>Yes</span>
              </label>
            </LabeledField>
          </div>
        )}

        {dataType === 'students' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <LabeledField label="Minimum sessions in range" isDark={isDark}>
              <Input type="number" min={0} value={studentsFilters.minSessions}
                onChange={e => setStudentsFilters(f => ({ ...f, minSessions: Number(e.target.value) || 0 }))} className="w-32" />
            </LabeledField>
            <LabeledField label="Engagement band" isDark={isDark}>
              <div className={chipWrap}>
                {ENGAGEMENT_BANDS.map(b => (
                  <button key={b} onClick={() => setStudentsFilters(f => ({ ...f, engagementBand: b }))}
                    className={`${chipBase} ${studentsFilters.engagementBand === b ? chipActive : chipIdle}`}>
                    {b}
                  </button>
                ))}
              </div>
            </LabeledField>
          </div>
        )}

        {dataType === 'issues' && (
          <div className="space-y-3">
            <LabeledField label="Categories" isDark={isDark}>
              <div className="flex flex-wrap gap-2">
                {CATEGORIES.map(c => {
                  const on = issuesFilters.categories.includes(c.key);
                  return (
                    <button key={c.key}
                      onClick={() => setIssuesFilters(f => ({
                        ...f,
                        categories: on ? f.categories.filter(x => x !== c.key) : [...f.categories, c.key],
                      }))}
                      className={`${chipBase} border ${on
                        ? isDark ? 'bg-[#27272a] border-[#3f3f46] text-white' : 'bg-[#e8e8e8] border-[#cccccc] text-[#1a1a1a]'
                        : isDark ? 'border-[#27272a] text-[#a1a1aa] hover:text-white' : 'border-[#e5e5e5] text-[#666] hover:text-[#1a1a1a]'}`}>
                      {c.label}
                    </button>
                  );
                })}
              </div>
            </LabeledField>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <LabeledField label="Min severity" isDark={isDark}>
                <Input type="number" min={0} max={10} value={issuesFilters.minSeverity}
                  onChange={e => setIssuesFilters(f => ({ ...f, minSeverity: Number(e.target.value) || 0 }))} className="w-24" />
              </LabeledField>
              <LabeledField label="Max severity" isDark={isDark}>
                <Input type="number" min={0} max={10} value={issuesFilters.maxSeverity}
                  onChange={e => setIssuesFilters(f => ({ ...f, maxSeverity: Number(e.target.value) || 0 }))} className="w-24" />
              </LabeledField>
              <LabeledField label="Status" isDark={isDark}>
                <div className={chipWrap}>
                  {STATUS_OPTIONS.map(s => (
                    <button key={s.key} onClick={() => setIssuesFilters(f => ({ ...f, status: s.key }))}
                      className={`${chipBase} ${issuesFilters.status === s.key ? chipActive : chipIdle}`}>
                      {s.label}
                    </button>
                  ))}
                </div>
              </LabeledField>
            </div>
            <label className="flex items-center gap-2 pt-1">
              <Checkbox checked={issuesFilters.includeSuppressed}
                onCheckedChange={(v) => setIssuesFilters(f => ({ ...f, includeSuppressed: !!v }))} />
              <span className={`text-sm ${muted}`}>Include issues suppressed by guardrails</span>
            </label>
          </div>
        )}

        {dataType === 'punctuality' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <LabeledField label="Who was late" isDark={isDark}>
              <div className={chipWrap}>
                {WHO_LATE_OPTIONS.map(o => (
                  <button key={o.key} onClick={() => setPunctualityFilters(f => ({ ...f, who: o.key }))}
                    className={`${chipBase} ${punctualityFilters.who === o.key ? chipActive : chipIdle}`}>
                    {o.label}
                  </button>
                ))}
              </div>
            </LabeledField>
            <LabeledField label="Minimum lateness (minutes)" isDark={isDark}>
              <Input type="number" min={0} value={punctualityFilters.minMinutes}
                onChange={e => setPunctualityFilters(f => ({ ...f, minMinutes: Number(e.target.value) || 0 }))} className="w-32" />
            </LabeledField>
          </div>
        )}
      </div>

      <div className={`${card} p-5 space-y-4`}>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className={stepLabel}>Step 4 — Columns</div>
            <div className={`text-xs mt-1 ${subtle}`}>
              {activeColumns.length} of {(columnsByType[dataType] || []).length} columns selected
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setAllColumnsForType(dataType, true)}
              className={`px-3 py-1.5 text-xs rounded-md border ${isDark ? 'border-[#27272a] text-[#a1a1aa] hover:text-white hover:bg-[#18181b]' : 'border-[#e5e5e5] text-[#666] hover:text-[#1a1a1a] hover:bg-[#f5f5f5]'}`}
            >
              Select all
            </button>
            <button
              type="button"
              onClick={() => setAllColumnsForType(dataType, false)}
              className={`px-3 py-1.5 text-xs rounded-md border ${isDark ? 'border-[#27272a] text-[#a1a1aa] hover:text-white hover:bg-[#18181b]' : 'border-[#e5e5e5] text-[#666] hover:text-[#1a1a1a] hover:bg-[#f5f5f5]'}`}
            >
              Clear
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
          {(columnsByType[dataType] || []).map(col => {
            const id = `export-column-${dataType}-${col.key}`;
            return (
              <label
                key={col.key}
                htmlFor={id}
                className={`flex items-start gap-2 rounded-md border px-3 py-2 cursor-pointer transition-colors ${
                  activeColumnKeySet.has(col.key)
                    ? isDark ? 'border-[#3f3f46] bg-[#18181b]' : 'border-[#d0d0d0] bg-[#f7f7f7]'
                    : isDark ? 'border-[#27272a] hover:border-[#3f3f46]' : 'border-[#e5e5e5] hover:border-[#d0d0d0]'
                }`}
              >
                <Checkbox
                  id={id}
                  checked={activeColumnKeySet.has(col.key)}
                  onCheckedChange={() => toggleColumn(dataType, col.key)}
                  className="mt-0.5"
                />
                <span className="min-w-0">
                  <span className={`block text-xs font-medium ${heading}`}>{col.label || col.header}</span>
                  <span className={`block text-[10px] mt-0.5 ${subtle}`}>{col.group || 'Column'} · {col.header}</span>
                </span>
              </label>
            );
          })}
        </div>
      </div>

      <div className={`${card} p-5 flex items-center justify-between gap-4 flex-wrap`}>
        <div>
          <div className={stepLabel}>Step 5 — Preview</div>
          <div className={`mt-2 text-lg font-semibold ${heading}`}>
            {previewLoading ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" /> Calculating…
              </span>
            ) : previewError ? (
              <span className="text-red-500 text-sm">{previewError}</span>
            ) : (
              <>
                {previewCount.toLocaleString()} row{previewCount === 1 ? '' : 's'}
                {capped && <span className={`ml-2 text-xs ${subtle}`}>(capped at {ROW_CAP.toLocaleString()})</span>}
              </>
            )}
          </div>
        </div>
        <Button onClick={handleDownload} disabled={previewLoading || previewCount === 0 || activeColumns.length === 0}>
          <Download className="h-4 w-4 mr-2" />
          Download CSV
        </Button>
      </div>
    </div>
  );
};

const LabeledField = ({ label, isDark, children }) => (
  <div className="space-y-1.5">
    <div className={`text-xs font-medium ${isDark ? 'text-[#a1a1aa]' : 'text-[#666666]'}`}>{label}</div>
    {children}
  </div>
);

export default Export;
